import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory'
import { ApolloLink, ApolloError } from 'apollo-boost'
import { Context, NuxtAppOptions } from '@nuxt/types'
import { ApolloClientOptions } from 'apollo-client/ApolloClient'
import { RetryLink } from 'apollo-link-retry'
import { ILogger } from '@qest/logger-utils'
import { FORMAT_HTTP_HEADERS, Tags } from 'opentracing'
import { v4 as uuidv4 } from 'uuid'
import introspectionQueryResultData from './../fragmentTypes.json'
import { AppConfig } from '~/config/appConfig.type'
import {
  ApolloQuery,
  mutation,
  onCacheInit,
  typeDefs,
} from '~/graphql/gqlLocal'
import { RequestWithServices } from '~/server/interfaces'
import { getUrbanFeatureFlagsHeaders } from '~/utils/urbanFeatureFlags'

export interface ApolloCtx {
  app: {
    $env: AppConfig
    $shopConfig: NuxtAppOptions['$shopConfig']
    $webApi: NuxtAppOptions['$webApi']
    context: Context
  }
  req?: RequestWithServices
  logger: ILogger
}

const apolloConfig = (ctx: ApolloCtx) => {
  const { app, req = null } = ctx

  const {
    graphqlUrbanHeaderShopId,
    graphqlUrbanPath,
    graphqlUseFullPathOnClient,
  } = app.$env

  const { $shopConfig } = app
  const browserHttpEndpoint = `${
    graphqlUseFullPathOnClient === 'true' ? $shopConfig?.shopUrl : ''
  }/${graphqlUrbanPath}`
  const httpEndpoint = `${app.$env.graphqlUrbanUrl}`

  const xForwardedFor = req?.headers['x-forwarded-for'] || req?.headers.host

  const headers = {
    ...(graphqlUrbanHeaderShopId
      ? { 'x-shop-id': graphqlUrbanHeaderShopId }
      : { 'x-shop-id': app.$shopConfig?.shopId }),
    ...{ 'x-user-id': $shopConfig?.user?.id ?? null },
    'x-user': $shopConfig?.user?.id ? JSON.stringify($shopConfig?.user) : null,
    ...($shopConfig?.cartId ? { 'x-cart-id': $shopConfig?.cartId } : {}),
    'x-trace-id': req?.traceId || uuidv4(),
    'x-is-bot': $shopConfig?.isBot,
    ...(process.server && { 'x-forwarded-for': xForwardedFor }),
    ...(process.server && {
      'user-agent': req?.headers['user-agent'] || 'mal-webkc',
    }),
    // per-request urban feature flag overrides from cookies (e.g. `parcel_tracking_timeline_v2=1`), see utils/urbanFeatureFlags.ts
    ...getUrbanFeatureFlagsHeaders($shopConfig?.cookies),
  }
  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData,
  })
  const cache = new InMemoryCache({ fragmentMatcher })

  const openTracingLink = new ApolloLink((operation, forward) => {
    const gqlSpan =
      req?.tracingSpan?.tracer().startSpan('GQL query', {
        childOf: req.tracingSpan,
      }) || null

    if (gqlSpan) {
      const headers = operation.getContext().headers || {}

      gqlSpan.tracer().inject(gqlSpan, FORMAT_HTTP_HEADERS, headers)

      operation.setContext({
        headers,
      })
    }

    return forward(operation).map((response) => {
      if (!gqlSpan) {
        return response
      }
      const keys = Object.keys(response.data || {})
      if (keys.length) {
        gqlSpan.setOperationName(`GQL ${keys.join(',')}`)
      }
      if (response.errors && response.errors.length > 0) {
        gqlSpan.setTag(Tags.ERROR, true)
      }
      gqlSpan.finish()
      return response
    })
  })

  return {
    browserHttpEndpoint,
    cache,
    connectToDevTools: true,
    httpEndpoint: process.client ? browserHttpEndpoint : httpEndpoint,
    httpLinkOptions: {
      credentials: 'same-origin',
      headers,
    },
    link: ApolloLink.from([
      openTracingLink,
      new RetryLink({
        attempts: {
          max: app.$env.apolloRetryLinkAttempts
            ? parseInt(app.$env.apolloRetryLinkAttempts, 10)
            : 5,
          retryIf: (error, _operation) => {
            if (_operation.getContext().disableRetry) {
              return false
            }
            if (process.server) {
              ctx.logger.warn(
                new ApolloError({
                  errorMessage: 'Query retry',
                  extraInfo: {
                    ...error,
                    ..._operation,
                  },
                })
              )
            }
            return !!error
          },
        },
        delay: {
          initial: app.$env.apolloRetryLinkDelay
            ? parseInt(app.$env.apolloRetryLinkDelay, 10)
            : 500,
          jitter: true,
          max: Infinity,
        },
      }),
    ]),

    onCacheInit: onCacheInit(app.$shopConfig?.csrftoken),
    resolvers: {
      Mutation: mutation(ctx),
      Query: ApolloQuery(ctx),
    },
    typeDefs,
  } as ApolloClientOptions<any>
}
export default apolloConfig
